import React, { useRef, useEffect, useState } from 'react';
import { Printer, Download, X, Copy, Check } from 'lucide-react';
import { APP_NAME } from '../config/frontend';

const POSReceipt = ({ 
  receiptData, 
  onClose, 
  onPrint, 
  onDownload,
  formatCurrency,
  receiptSettings = {
    company_name: 'Grovalue Enterprise',
    company_address: '123 Business Street, City, Country',
    company_phone: '+233 XX XXX XXXX',
    company_email: '',
    company_website: '',
    tax_number: '',
    currency_symbol: 'Ghs',
    receipt_footer: `Thank you for your business!\nKeep this receipt for your records\nPowered by ${APP_NAME} System`
  }
}) => {
  const receiptRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleCopyReceipt = async () => {
    if (receiptRef.current) {
      try {
        await navigator.clipboard.writeText(receiptRef.current.innerText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy receipt:', err);
      }
    }
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint(receiptRef.current);
    } else {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>POS Receipt - ${receiptData.reference_number}</title>
            <style>
              @page {
                size: 80mm 200mm;
                margin: 0;
              }
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                margin: 0; 
                padding: 10px;
                background: white;
                width: 80mm;
                line-height: 1.2;
              }
              .receipt { 
                width: 100%; 
                margin: 0 auto; 
                padding: 0;
              }
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              .font-bold { font-weight: bold; }
              .border-t { border-top: 1px solid #000; }
              .border-b { border-bottom: 1px solid #000; }
              .mb-1 { margin-bottom: 2px; }
              .mb-2 { margin-bottom: 4px; }
              .mb-3 { margin-bottom: 6px; }
              .mb-4 { margin-bottom: 8px; }
              .mb-6 { margin-bottom: 12px; }
              .mt-1 { margin-top: 2px; }
              .mt-2 { margin-top: 4px; }
              .mt-3 { margin-top: 6px; }
              .mt-6 { margin-top: 12px; }
              .pt-2 { padding-top: 4px; }
              .pt-3 { padding-top: 6px; }
              .pt-4 { padding-top: 8px; }
              .py-3 { padding-top: 6px; padding-bottom: 6px; }
              .flex { display: flex; }
              .justify-between { justify-content: space-between; }
              .items-start { align-items: flex-start; }
              .w-1-2 { width: 50%; }
              .w-1-6 { width: 16.666667%; }
              .pr-2 { padding-right: 4px; }
              .text-xs { font-size: 10px; }
              .text-sm { font-size: 12px; }
              .text-lg { font-size: 14px; }
              .text-xl { font-size: 16px; }
              .leading-tight { line-height: 1.1; }
              .tracking-wider { letter-spacing: 0.05em; }
              .text-gray-600 { color: #4b5563; }
              .text-gray-700 { color: #374151; }
              .text-gray-500 { color: #6b7280; }
              .text-gray-400 { color: #9ca3af; }
              .text-red-600 { color: #dc2626; }
              .text-green-600 { color: #16a34a; }
              @media print {
                body { margin: 0; padding: 5px; }
                .receipt { border: none; }
              }
            </style>
          </head>
          <body>
            <div class="receipt">${receiptRef.current.innerHTML}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(receiptRef.current);
    }
  };

  if (!receiptData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Receipt Data</h3>
            <p className="text-gray-500 mb-4">No receipt data available to display.</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Printer className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">POS Receipt</h3>
              <p className="text-sm text-gray-500">Transaction #{receiptData.reference_number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Receipt Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div 
            ref={receiptRef}
            className="bg-white border border-gray-200 rounded-lg p-6 font-mono text-sm thermal-receipt"
            style={{ 
              minWidth: '300px',
              maxWidth: '400px',
              margin: '0 auto',
              fontFamily: 'Courier New, monospace',
              lineHeight: '1.4'
            }}
          >
            {/* Receipt Header */}
            <div className="text-center mb-6">
              <div className="text-lg font-bold mb-2 tracking-wider">{APP_NAME} SYSTEM</div>
              <div className="text-xs text-gray-700 font-medium">
                {receiptData.company_name || 'Grovalue Enterprise'}
              </div>
              <div className="text-xs text-gray-600">
                {receiptData.company_address || '123 Business Street, City, Country'}
              </div>
              <div className="text-xs text-gray-600">
                Tel: {receiptData.company_phone || '+233 XX XXX XXXX'}
              </div>
              <div className="border-t border-gray-400 my-3"></div>
            </div>

            {/* Receipt Details */}
            <div className="mb-4">
              <div className="flex justify-between mb-1 text-xs">
                <span>Receipt #:</span>
                <span className="font-bold">{receiptData.reference_number}</span>
              </div>
              <div className="flex justify-between mb-1 text-xs">
                <span>Date:</span>
                <span>{receiptData.date}</span>
              </div>
              <div className="flex justify-between mb-1 text-xs">
                <span>Time:</span>
                <span>{receiptData.time}</span>
              </div>
              <div className="flex justify-between mb-1 text-xs">
                <span>Cashier:</span>
                <span>{receiptData.cashier_name}</span>
              </div>
              <div className="flex justify-between mb-2 text-xs">
                <span>Payment:</span>
                <span>{receiptData.payment_method}</span>
              </div>
              <div className="border-t border-gray-400"></div>
            </div>

            {/* Items */}
            <div className="mb-4">
              <div className="mb-3">
                <div className="flex justify-between text-xs font-bold">
                  <span className="w-1/2">ITEM</span>
                  <span className="w-1/6 text-center">QTY</span>
                  <span className="w-1/6 text-center">UOM</span>
                  <span className="w-1/6 text-right">AMOUNT</span>
                </div>
                <div className="border-t border-gray-400 mt-1"></div>
              </div>
              
              {receiptData.items.map((item, index) => (
                <div key={index} className="mb-2">
                  <div className="flex justify-between items-start">
                    <div className="w-1/2 pr-2">
                      <div className="font-medium text-xs leading-tight">{item.name}</div>
                      <div className="text-xs text-gray-600">
                        @ {formatCurrency(item.price)}/{item.uom}
                      </div>
                    </div>
                    <div className="w-1/6 text-center text-xs">{item.quantity}</div>
                    <div className="w-1/6 text-center text-xs">{item.uom}</div>
                    <div className="w-1/6 text-right text-xs font-medium">
                      {formatCurrency(item.amount)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-gray-400 pt-3">
              <div className="flex justify-between mb-1 text-xs">
                <span>Subtotal:</span>
                <span>{formatCurrency(receiptData.subtotal)}</span>
              </div>
              {receiptData.discount > 0 && (
                <div className="flex justify-between mb-1 text-xs text-red-600">
                  <span>Discount:</span>
                  <span>-{formatCurrency(receiptData.discount)}</span>
                </div>
              )}
              {receiptData.vat > 0 && (
                <div className="flex justify-between mb-1 text-xs">
                  <span>VAT ({receiptData.vat_rate || 15}%):</span>
                  <span>{formatCurrency(receiptData.vat)}</span>
                </div>
              )}
              <div className="border-t border-gray-400 pt-2 mt-2">
                <div className="flex justify-between font-bold text-sm">
                  <span>TOTAL:</span>
                  <span>{formatCurrency(receiptData.total_amount)}</span>
                </div>
              </div>
              <div className="flex justify-between mt-2 text-xs">
                <span>Amount Paid:</span>
                <span>{formatCurrency(receiptData.amount_paid)}</span>
              </div>
              {receiptData.change_amount > 0 && (
                <div className="flex justify-between font-bold text-xs text-green-600">
                  <span>Change:</span>
                  <span>{formatCurrency(receiptData.change_amount)}</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="text-center mt-6 pt-4 border-t border-gray-400">
              {receiptSettings.receipt_footer.split('\n').map((line, index) => (
                <div key={index} className="text-xs text-gray-600 mb-1">
                  {line}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCopyReceipt}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleDownload}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSReceipt;
